import { supabase } from '@/lib/supabase'
import { paymentMethodsApi } from '@/api/paymentMethods'

export type SubscriberDecision =
  | 'none'
  | 'charge'
  | 'extend_trial'
  | 'downgrade'
  | 'suspend'
  | 'free'
  | 'follow_up'

export type SubscriberFlag =
  | 'in_grace'
  | 'no_card'
  | 'unpaid'
  | 'partial'
  | 'needs_review'
  | 'unknown'
  | 'trial_ending'
  | 'expired_in_use'
  | 'mismatch'
  | 'trial_no_card'
  | 'renewal_soon'
  | 'not_linked'

export interface SubscriberRow {
  organization_id: string
  org_name: string
  owner_name: string | null
  owner_email: string | null
  plan_code: string
  plan_name: string
  plan_family: string
  status: string
  trial_end: string | null
  billing_cycle: string | null
  net_agorot: number
  vat_agorot: number
  gross_agorot: number
  vat_percent: number
  benefit_label: string | null
  first_charge_status: string | null
  first_charge_at: string | null
  first_charge_amount_agorot: number | null
  first_charge_period_key: string | null
  last_charge_at: string | null
  last_charge_amount_agorot: number | null
  last_charge_invoice_numbers: string | null
  last_charge_invoice_url: string | null
  open_balance_agorot: number
  next_charge_at: string | null
  grace_started_at: string | null
  grace_ends_at: string | null
  grace_reason: string | null
  grace_days_left: number | null
  card_last4: string | null
  card_brand: string | null
  /** Has an otomator_customer_accounts row — only then can a card be saved/read. */
  billing_linked: boolean
  wa_status: string | null
  period_status: string | null
  flags: SubscriberFlag[]
  decision: SubscriberDecision
  decision_note: string
  decided_at: string | null
  decided_by: string | null
}

export interface SubscriberDecisionLogEntry {
  organization_id: string
  decision: SubscriberDecision
  note: string
  decided_at: string
  decided_by: string | null
}

export interface SubscriberTrialExtension {
  id: string
  organization_id: string
  subscription_id: string
  previous_end: string | null
  new_end: string
  days: number | null
  note: string
  extended_by: string | null
  extended_by_email: string | null
  created_at: string
}

export interface SubscriberBillingPeriod {
  period_key: string
  amount_agorot: number
  status: string
  document_url: string | null
  sumit_document_number: string | null
  charge_due_at: string | null
  net_agorot: number
  vat_agorot: number
  gross_agorot: number
}

export interface SubscriberStatusCounts {
  source: 'all_systems' | 'jumpstart'
  active: number
  trial: number
  expired: number
  cancelled: number
  suspended: number
  other: number
}

export interface SubscribersPayload {
  rows: SubscriberRow[]
  countsAllSystems: SubscriberStatusCounts
  countsJumpstart: SubscriberStatusCounts
  persistence: 'rpc' | 'local'
}

const DECISION_KEY = 'admin-subscriber-decisions-v1'
const DECISION_LOG_KEY = 'admin-subscriber-decision-log-v1'
const DECISIONS: SubscriberDecision[] = [
  'none', 'charge', 'extend_trial', 'downgrade', 'suspend', 'free', 'follow_up',
]

function isMissingRpc(err: { code?: string; message?: string } | null): boolean {
  const msg = err?.message ?? ''
  return err?.code === 'PGRST202' || /could not find the function|schema cache/i.test(msg)
}

function asDecision(v: unknown): SubscriberDecision {
  return DECISIONS.includes(v as SubscriberDecision) ? (v as SubscriberDecision) : 'none'
}

function planFamily(code: string, catalogName?: string | null): string {
  const c = (code ?? '').toLowerCase()
  if (c.startsWith('accelerate') || c === 'pro') return 'Accelerate'
  if (c.startsWith('ignite') || c === 'basic') return 'Ignite'
  if (c.startsWith('command') || c === 'business') return 'Command'
  return catalogName || code
}

function emptyCounts(source: SubscriberStatusCounts['source']): SubscriberStatusCounts {
  return { source, active: 0, trial: 0, expired: 0, cancelled: 0, suspended: 0, other: 0 }
}

function tally(rows: Array<{ status?: string | null }>, source: SubscriberStatusCounts['source']): SubscriberStatusCounts {
  const c = emptyCounts(source)
  for (const r of rows) {
    const s = (r.status ?? '').toLowerCase()
    if (s === 'active') c.active += 1
    else if (s === 'trial' || s === 'trialing') c.trial += 1
    else if (s === 'expired') c.expired += 1
    else if (s === 'cancelled') c.cancelled += 1
    else if (s === 'suspended') c.suspended += 1
    else c.other += 1
  }
  return c
}

type LocalDecision = { decision: SubscriberDecision; note: string; decided_at: string; decided_by: string | null }

function loadDecisionLog(): SubscriberDecisionLogEntry[] {
  try {
    const raw = localStorage.getItem(DECISION_LOG_KEY)
    const parsed = raw ? (JSON.parse(raw) as SubscriberDecisionLogEntry[]) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function appendDecisionLog(entry: SubscriberDecisionLogEntry): void {
  try {
    const log = loadDecisionLog()
    log.push(entry)
    localStorage.setItem(DECISION_LOG_KEY, JSON.stringify(log.slice(-2000)))
  } catch {
    /* browser storage unavailable — history stays in memory only */
  }
}

function loadLocalDecisions(): Record<string, LocalDecision> {
  try {
    const raw = localStorage.getItem(DECISION_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, { decision?: string; note?: string; decided_at?: string; decided_by?: string | null }>
    const out: Record<string, LocalDecision> = {}
    for (const [id, v] of Object.entries(parsed)) {
      out[id] = {
        decision: asDecision(v.decision),
        note: typeof v.note === 'string' ? v.note : '',
        decided_at: v.decided_at ?? new Date().toISOString(),
        decided_by: v.decided_by ?? null,
      }
    }
    return out
  } catch {
    return {}
  }
}

function saveLocalDecision(orgId: string, decision: SubscriberDecision, note: string, by: string | null, at: string): void {
  const all = loadLocalDecisions()
  all[orgId] = { decision, note, decided_at: at, decided_by: by }
  localStorage.setItem(DECISION_KEY, JSON.stringify(all))
}

function mergeLocal(rows: SubscriberRow[]): SubscriberRow[] {
  const local = loadLocalDecisions()
  return rows.map((row) => {
    const d = local[row.organization_id]
    if (!d) return row
    return { ...row, decision: d.decision, decision_note: d.note, decided_at: d.decided_at, decided_by: d.decided_by }
  })
}

function num(v: unknown): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string' && v.trim() !== '') return Number(v)
  return 0
}

async function mapInBatches<T, R>(items: T[], size: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = []
  for (let i = 0; i < items.length; i += size) {
    const chunk = items.slice(i, i + size)
    out.push(...await Promise.all(chunk.map(fn)))
  }
  return out
}

type PeriodRow = {
  period_key: string
  amount_agorot: number
  status: string
  document_url: string | null
  sumit_document_number: string | null
  breakdown: Record<string, unknown> | null
  charge_due_at: string | null
}

function moneyFromBreakdown(breakdown: Record<string, unknown> | null | undefined, fallbackGross = 0): {
  net: number
  vat: number
  gross: number
  vatPercent: number
  benefit: string | null
} {
  const b = breakdown ?? {}
  const vatPercent = num(b.vat_percent) || 18
  const net = num(b.net_total_agorot) || num(b.subtotal_agorot) || num(b.effective_price_agorot) || num(b.total_agorot)
  const vat = num(b.vat_agorot)
  const gross = num(b.total_agorot) && num(b.vat_agorot)
    ? (num(b.net_total_agorot) ? num(b.total_agorot) : net + vat)
    : fallbackGross || net
  const benefit =
    (b.benefit && typeof b.benefit === 'object' && b.benefit !== null && 'reason_customer' in b.benefit
      ? String((b.benefit as { reason_customer?: unknown }).reason_customer ?? '')
      : '')
    || (Array.isArray(b.benefits) && b.benefits[0] && typeof b.benefits[0] === 'object'
      ? String((b.benefits[0] as { reason_customer?: unknown }).reason_customer ?? '')
      : '')
    || null
  return {
    net,
    vat: vat || Math.round(net * vatPercent / 100),
    gross: gross || net + Math.round(net * vatPercent / 100),
    vatPercent,
    benefit: benefit || null,
  }
}

async function listStatusCounts(): Promise<{ all: SubscriberStatusCounts; jumpstart: SubscriberStatusCounts }> {
  const { data, error } = await supabase
    .from('org_system_subscriptions')
    .select('system_code, status')
  if (error) throw error
  const rows = (data ?? []) as Array<{ system_code: string; status: string }>
  return {
    all: tally(rows, 'all_systems'),
    jumpstart: tally(rows.filter((r) => r.system_code === 'jumpstart'), 'jumpstart'),
  }
}

async function listViaExistingReads(): Promise<SubscriberRow[]> {
  const { data: subs, error: subErr } = await supabase
    .from('org_system_subscriptions')
    .select('id, organization_id, system_code, plan_code, status, billing_cycle, effective_price_agorot, override_reason, expires_at, created_at, first_charge_status, first_charge_at, first_charge_amount_agorot, first_charge_period_key, next_charge_at, grace_started_at, grace_ends_at, grace_reason')
    .eq('system_code', 'jumpstart')
  if (subErr) throw subErr

  const ranked = [...(subs ?? [])].sort((a, b) => {
    const rank = (s: string) =>
      s === 'active' ? 1 : s === 'trial' || s === 'trialing' ? 2 : s === 'suspended' ? 3 : s === 'expired' ? 4 : 5
    const d = rank(a.status) - rank(b.status)
    if (d !== 0) return d
    return String(b.created_at).localeCompare(String(a.created_at))
  })
  const byOrg = new Map<string, (typeof ranked)[number]>()
  for (const row of ranked) {
    if (!byOrg.has(row.organization_id)) byOrg.set(row.organization_id, row)
  }
  const chosen = [...byOrg.values()]
  const orgIds = chosen.map((s) => s.organization_id)

  const [{ data: orgs }, { data: plans }, { data: waRows }, { data: runs }, { data: payments }, { data: charges }] =
    await Promise.all([
      supabase.from('organizations').select('id, name, owner_user_id, trial_expires_at').in('id', orgIds),
      supabase.from('system_license_plans').select('code, name').eq('system_code', 'jumpstart'),
      supabase.from('org_system_subscriptions').select('organization_id, status, created_at').eq('system_code', 'whatsapp').in('organization_id', orgIds),
      supabase.from('customer_billing_runs').select('customer_org_id, status, remaining_agorot, charged_agorot, outcome, payment_id, error_detail, manual_review_required, period_key, amount_agorot, breakdown, created_at').in('customer_org_id', orgIds),
      supabase.from('customer_billing_period_payments').select('org_id, period_key, status, amount_agorot, provider_document_number, created_at').in('org_id', orgIds).eq('status', 'succeeded'),
      supabase.from('client_billing_charges').select('org_id, provider_document_number, sumit_invoice_url, amount_agorot, charged_at, remaining_agorot, display_status').in('org_id', orgIds),
    ])

  const ownerIds = [...new Set((orgs ?? []).map((o: { owner_user_id: string | null }) => o.owner_user_id).filter(Boolean))] as string[]
  const { data: users } = ownerIds.length
    ? await supabase.from('users').select('id, email, first_name, last_name').in('id', ownerIds)
    : { data: [] as Array<{ id: string; email: string; first_name: string | null; last_name: string | null }> }

  const orgById = new Map((orgs ?? []).map((o: { id: string; name: string; owner_user_id: string | null; trial_expires_at: string | null }) => [o.id, o]))
  const userById = new Map((users ?? []).map((u) => [u.id, u]))
  const planByCode = new Map((plans ?? []).map((p: { code: string; name: string }) => [p.code, p.name]))

  const waByOrg = new Map<string, string>()
  const waSorted = [...((waRows ?? []) as Array<{ organization_id: string; status: string; created_at: string }>)].sort(
    (a, b) => String(b.created_at).localeCompare(String(a.created_at)),
  )
  for (const w of waSorted) {
    if (!waByOrg.has(w.organization_id)) waByOrg.set(w.organization_id, w.status)
  }

  const periods = await mapInBatches(orgIds, 8, async (orgId) => {
    const { data, error } = await supabase.rpc('list_customer_billing_periods', { p_customer_org: orgId })
    if (error) return { orgId, rows: [] as PeriodRow[] }
    return { orgId, rows: (data ?? []) as PeriodRow[] }
  })
  const periodsByOrg = new Map(periods.map((p) => [p.orgId, p.rows]))

  const { data: bridgeRows, error: bridgeErr } = await supabase
    .from('otomator_customer_accounts')
    .select('customer_org_id')
    .in('customer_org_id', orgIds)
  // If the bridge can't be read for this session (RLS/error/empty), don't guess — treat all as linked and ask the vault.
  const bridgeKnown = !bridgeErr && (bridgeRows ?? []).length > 0
  const linked = bridgeKnown
    ? new Set(((bridgeRows ?? []) as Array<{ customer_org_id: string }>).map((b) => b.customer_org_id))
    : new Set(orgIds)

  // The card vault answers 403 for orgs without a billing link — only ask for linked orgs.
  const cards = await mapInBatches(orgIds.filter((id) => linked.has(id)), 6, async (orgId) => {
    try {
      const s = await paymentMethodsApi.status(orgId)
      return { orgId, last4: s.last4, brand: s.brand }
    } catch {
      return { orgId, last4: null as string | null, brand: null as string | null }
    }
  })
  const cardByOrg = new Map(cards.map((c) => [c.orgId, c]))

  const now = Date.now()
  const in7 = now + 7 * 24 * 60 * 60 * 1000

  return chosen.map((sub) => {
    const org = orgById.get(sub.organization_id)
    const owner = org?.owner_user_id ? userById.get(org.owner_user_id) : undefined
    const ownerName = owner
      ? [owner.first_name, owner.last_name].filter(Boolean).join(' ').trim() || null
      : null
    const catalogName = planByCode.get(sub.plan_code) ?? null
    const family = planFamily(sub.plan_code, catalogName)
    const periodRows = periodsByOrg.get(sub.organization_id) ?? []
    const current = periodRows[0] ?? null
    const money = moneyFromBreakdown(
      current?.breakdown ?? null,
      current?.amount_agorot ?? 0,
    )
    const orgRuns = ((runs ?? []) as Array<{
      customer_org_id: string
      remaining_agorot: number | null
      charged_agorot: number | null
      status: string
      period_key: string
      manual_review_required: boolean | null
      created_at: string
    }>).filter((r) => r.customer_org_id === sub.organization_id)
    const open = orgRuns.reduce((s, r) => s + (r.remaining_agorot ?? 0), 0)
    const latestRun = [...orgRuns].sort((a, b) => b.period_key.localeCompare(a.period_key))[0]
    const orgPays = ((payments ?? []) as Array<{
      org_id: string
      period_key: string
      amount_agorot: number
      provider_document_number: string | null
      created_at: string
    }>).filter((p) => p.org_id === sub.organization_id)
    const latestPayKey = [...orgPays].sort((a, b) => b.created_at.localeCompare(a.created_at))[0]?.period_key
    const lastPays = latestPayKey ? orgPays.filter((p) => p.period_key === latestPayKey) : []
    const lastAmount = lastPays.reduce((s, p) => s + (p.amount_agorot ?? 0), 0)
    const lastAt = lastPays.reduce<string | null>((m, p) => (!m || p.created_at > m ? p.created_at : m), null)
    const lastNums = lastPays.map((p) => p.provider_document_number).filter(Boolean).join(' + ') || null
    const orgCharges = ((charges ?? []) as Array<{
      org_id: string
      provider_document_number: string | null
      sumit_invoice_url: string | null
    }>).filter((c) => c.org_id === sub.organization_id)
    const lastUrl = lastPays
      .map((p) => orgCharges.find((c) => c.provider_document_number === p.provider_document_number)?.sumit_invoice_url)
      .find((u) => !!u) ?? null
    const card = cardByOrg.get(sub.organization_id)
    const waStatus = waByOrg.get(sub.organization_id) ?? null
    const trialEnd = sub.expires_at ?? org?.trial_expires_at ?? null
    const trialMs = trialEnd ? new Date(trialEnd).getTime() : NaN
    const flags: SubscriberFlag[] = []
    const graceDaysLeft = sub.grace_reason === 'no_card' && sub.grace_ends_at
      ? Math.max(0, Math.ceil((new Date(sub.grace_ends_at).getTime() - now) / (24 * 60 * 60 * 1000)))
      : null
    if (sub.status === 'active' && graceDaysLeft != null) flags.push('in_grace')
    const paying = sub.status === 'active' && money.gross > 0
    const billingLinked = linked.has(sub.organization_id)
    if (!billingLinked) flags.push('not_linked')
    else if (paying && !card?.last4) flags.push('no_card')
    if (open > 0 && (latestRun?.charged_agorot ?? 0) > 0) flags.push('partial')
    else if (open > 0 || current?.status === 'unpaid' || current?.status === 'failed') flags.push('unpaid')
    if (latestRun?.manual_review_required || sub.first_charge_status === 'failed'
      || (sub.first_charge_status === 'open_manual' && open > 0)) {
      flags.push('needs_review')
    }
    if (current && !['paid', 'future', 'unpaid', 'failed'].includes(current.status)) flags.push('unknown')
    else if (paying && !current && sub.first_charge_at && new Date(sub.first_charge_at).getTime() <= now) flags.push('unknown')
    if ((sub.status === 'trial' || sub.status === 'trialing') && Number.isFinite(trialMs) && trialMs > now && trialMs <= in7) {
      flags.push('trial_ending')
    }
    if (['active', 'trial', 'trialing'].includes(sub.status) && waStatus && ['expired', 'cancelled', 'suspended'].includes(waStatus)) {
      flags.push('mismatch')
    }

    return {
      organization_id: sub.organization_id,
      org_name: org?.name ?? sub.organization_id,
      owner_name: ownerName,
      owner_email: owner?.email ?? null,
      plan_code: sub.plan_code,
      plan_name: catalogName ?? family,
      plan_family: family,
      status: sub.status,
      trial_end: trialEnd,
      billing_cycle: sub.billing_cycle,
      net_agorot: money.net,
      vat_agorot: money.vat,
      gross_agorot: money.gross,
      vat_percent: money.vatPercent,
      benefit_label: money.benefit || sub.override_reason,
      first_charge_status: sub.first_charge_status,
      first_charge_at: sub.first_charge_at,
      first_charge_amount_agorot: sub.first_charge_amount_agorot,
      first_charge_period_key: sub.first_charge_period_key,
      last_charge_at: lastAt,
      last_charge_amount_agorot: lastPays.length ? lastAmount : null,
      last_charge_invoice_numbers: lastNums,
      last_charge_invoice_url: lastUrl,
      open_balance_agorot: open,
      next_charge_at: sub.next_charge_at,
      grace_started_at: sub.grace_started_at,
      grace_ends_at: sub.grace_ends_at,
      grace_reason: sub.grace_reason,
      grace_days_left: graceDaysLeft,
      card_last4: card?.last4 ?? null,
      card_brand: card?.brand ?? null,
      billing_linked: billingLinked,
      wa_status: waStatus,
      period_status: current?.status ?? null,
      flags,
      decision: 'none',
      decision_note: '',
      decided_at: null,
      decided_by: null,
    }
  })
}

export const subscribersApi = {
  list: async (): Promise<SubscribersPayload> => {
    const counts = await listStatusCounts()
    const rows = await listViaExistingReads()
    const { data, error } = await supabase.rpc('admin_list_subscriber_decisions')
    if (!error) {
      const decisions = new Map(((data ?? []) as Record<string, unknown>[]).map((decision) => [
        String(decision.organization_id),
        decision,
      ]))
      const merged = rows.map((row) => {
        const decision = decisions.get(row.organization_id)
        if (!decision) return row
        return {
          ...row,
          decision: asDecision(decision.decision),
          decision_note: typeof decision.note === 'string' ? decision.note : '',
          decided_at: (decision.decided_at as string | null) ?? null,
          decided_by: (decision.decided_by_email as string | null) ?? null,
        }
      })
      return {
        rows: merged,
        countsAllSystems: counts.all,
        countsJumpstart: counts.jumpstart,
        persistence: 'rpc',
      }
    }
    if (!isMissingRpc(error)) throw error
    return {
      rows: mergeLocal(rows),
      countsAllSystems: counts.all,
      countsJumpstart: counts.jumpstart,
      persistence: 'local',
    }
  },

  saveDecision: async (
    organizationId: string,
    decision: SubscriberDecision,
    note: string,
    author: string | null = null,
  ): Promise<{ persistence: 'rpc' | 'local' }> => {
    const at = new Date().toISOString()
    appendDecisionLog({ organization_id: organizationId, decision, note, decided_at: at, decided_by: author })
    const { error } = await supabase.rpc('admin_upsert_subscriber_decision', {
      p_organization_id: organizationId,
      p_decision: decision,
      p_note: note,
    })
    if (!error) return { persistence: 'rpc' }
    if (!isMissingRpc(error)) throw error
    saveLocalDecision(organizationId, decision, note, author, at)
    return { persistence: 'local' }
  },

  /** Decision history recorded on this browser (newest first). */
  decisionLog: (organizationId: string): SubscriberDecisionLogEntry[] =>
    loadDecisionLog()
      .filter((e) => e.organization_id === organizationId)
      .sort((a, b) => b.decided_at.localeCompare(a.decided_at)),

  extendTrial: async (
    organizationId: string,
    input: { days: number | null; until: string | null; note: string },
  ): Promise<SubscriberTrialExtension> => {
    const { data, error } = await supabase.rpc('admin_extend_org_trial', {
      p_organization_id: organizationId,
      p_days: input.days,
      p_until: input.until,
      p_note: input.note,
    })
    if (error) throw error
    return data as SubscriberTrialExtension
  },

  trialExtensionHistory: async (organizationId: string): Promise<SubscriberTrialExtension[]> => {
    const { data, error } = await supabase.rpc('admin_list_org_trial_extensions', {
      p_organization_id: organizationId,
    })
    if (error) {
      if (isMissingRpc(error)) return []
      throw error
    }
    return (data ?? []) as SubscriberTrialExtension[]
  },

  /** Read-only: the same periods the org billing tab shows. No amount is recomputed here. */
  billingHistory: async (organizationId: string): Promise<SubscriberBillingPeriod[]> => {
    const { data, error } = await supabase.rpc('list_customer_billing_periods', { p_customer_org: organizationId })
    if (error) throw error
    return ((data ?? []) as PeriodRow[]).map((p) => {
      const m = moneyFromBreakdown(p.breakdown, p.amount_agorot ?? 0)
      return {
        period_key: p.period_key,
        amount_agorot: num(p.amount_agorot),
        status: p.status,
        document_url: p.document_url,
        sumit_document_number: p.sumit_document_number,
        charge_due_at: p.charge_due_at,
        net_agorot: m.net,
        vat_agorot: m.vat,
        gross_agorot: m.gross,
      }
    })
  },
}
